/**
 * Copyright © 2026 Adnuntius AS.
 */

export interface IsoWeekOption {
  value: string;
  startDate: string;
  endDate: string;
  label: string;
}

export const MAXIMUM_CUSTOM_PERIOD_DAYS = 60;

export type AdnInvoicingPeriodType = 'MONTH' | 'WEEK' | 'CUSTOM';

export function invoicingPeriodDateRangeLabel(periodType: AdnInvoicingPeriodType, periodValue: string): string {
  const range = invoicingPeriodDateRange(periodType, periodValue);
  return range
    ? `${formatDisplayDate(parseIsoDate(range.startDate))} to ${formatDisplayDate(parseIsoDate(range.endDate))}`
    : '';
}

export function invoicingPeriodTypeLabel(periodType: AdnInvoicingPeriodType): string {
  return periodType.charAt(0) + periodType.slice(1).toLowerCase();
}

export function validCustomPeriod(startDate: string, endDate: string): boolean {
  const start = Date.parse(`${startDate}T00:00:00.000Z`);
  const end = Date.parse(`${endDate}T00:00:00.000Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return false;
  return Math.floor((end - start) / 86400000) + 1 <= MAXIMUM_CUSTOM_PERIOD_DAYS;
}

export function previousMonth(date = new Date()): string {
  return formatMonth(new Date(date.getFullYear(), date.getMonth() - 1, 1));
}

export function previousIsoWeekValue(date = new Date(), timeZone = 'UTC'): string {
  const week = previousIsoWeek(date, timeZone);
  return `${week.year}-W${pad(week.weekNumber)}`;
}

export function recentIsoWeekOptions(count = 104, date = new Date(), timeZone = 'UTC'): IsoWeekOption[] {
  const firstMonday = previousIsoWeek(date, timeZone).monday;
  return Array.from({length: count}, (_, index) => {
    const monday = new Date(firstMonday);
    monday.setUTCDate(monday.getUTCDate() - (index * 7));
    const sunday = new Date(monday);
    sunday.setUTCDate(sunday.getUTCDate() + 6);
    const week = isoWeekForCalendarDate(monday);
    const value = `${week.year}-W${pad(week.weekNumber)}`;
    return {
      value,
      startDate: formatDate(monday),
      endDate: formatDate(sunday),
      label: `${value} — ${formatDisplayDate(monday)} to ${formatDisplayDate(sunday)}`
    };
  });
}

function invoicingPeriodDateRange(
  periodType: AdnInvoicingPeriodType,
  periodValue: string
): {startDate: string; endDate: string} | null {
  if (periodType === 'CUSTOM') {
    const [startDate, endDate, ...remaining] = periodValue.split('/');
    return !remaining.length && isIsoDate(startDate) && isIsoDate(endDate) && startDate <= endDate
      ? {startDate, endDate}
      : null;
  }

  if (periodType === 'MONTH') {
    const match = /^(\d{4})-(\d{2})$/.exec(periodValue);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) return null;
    return {
      startDate: formatDate(new Date(Date.UTC(year, month - 1, 1))),
      endDate: formatDate(new Date(Date.UTC(year, month, 0)))
    };
  }

  const match = /^(\d{4})-W(\d{2})$/.exec(periodValue);
  if (!match) return null;
  const year = Number(match[1]);
  const weekNumber = Number(match[2]);
  const januaryFourth = new Date(Date.UTC(year, 0, 4));
  const firstMonday = new Date(januaryFourth);
  firstMonday.setUTCDate(januaryFourth.getUTCDate() - (januaryFourth.getUTCDay() || 7) + 1);
  const monday = new Date(firstMonday);
  monday.setUTCDate(firstMonday.getUTCDate() + ((weekNumber - 1) * 7));
  const resolvedWeek = isoWeekForCalendarDate(monday);
  if (resolvedWeek.year !== year || resolvedWeek.weekNumber !== weekNumber) return null;
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return {startDate: formatDate(monday), endDate: formatDate(sunday)};
}

function previousIsoWeek(date: Date, timeZone: string): {year: number; weekNumber: number; monday: Date} {
  const localDate = calendarDateInTimeZone(date, timeZone);
  const dayOfWeek = localDate.getUTCDay() || 7;
  localDate.setUTCDate(localDate.getUTCDate() - dayOfWeek - 6);
  return isoWeekForCalendarDate(localDate);
}

function isoWeekForCalendarDate(date: Date): {year: number; weekNumber: number; monday: Date} {
  const thursday = new Date(date);
  const dayOfWeek = thursday.getUTCDay() || 7;
  thursday.setUTCDate(thursday.getUTCDate() + 4 - dayOfWeek);
  const year = thursday.getUTCFullYear();
  const januaryFourth = new Date(Date.UTC(year, 0, 4));
  const januaryFourthDay = januaryFourth.getUTCDay() || 7;
  const firstThursday = new Date(januaryFourth);
  firstThursday.setUTCDate(januaryFourth.getUTCDate() + 4 - januaryFourthDay);
  const weekNumber = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86400000));
  const monday = new Date(date);
  monday.setUTCDate(monday.getUTCDate() - dayOfWeek + 1);
  return {year, weekNumber, monday};
}

function calendarDateInTimeZone(date: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {timeZone, year: 'numeric', month: '2-digit', day: '2-digit'}).formatToParts(date);
  const value = (type: string) => Number.parseInt(parts.find((part) => part.type === type)?.value || '', 10);
  return new Date(Date.UTC(value('year'), value('month') - 1, value('day')));
}

function formatMonth(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatDisplayDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'});
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && formatDate(parseIsoDate(value)) === value;
}

function parseIsoDate(value: string): Date {
  return new Date(`${value}T00:00:00.000Z`);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
